<template>
  <lb-drawer
    :value="value"
    :heading="dialogHeading"
    width="600"
    :loading="loading"
    data-testid="bookclosure-project-list__add-cycle-drawer"
    @input="onInput"
  >
    <template v-slot:body>
      <div class="cycle-form-dialog" data-testid="bookclosure-project-list__add-cycle-form">
        <!-- Period (Figma single field + dual-month popover) -->
        <div class="cycle-form-dialog__field" data-testid="cycles-page__form-period-field">
          <PeriodRangePicker
            :from="form.from"
            :to="form.to"
            :error="periodError"
            :disabled="loading"
            @input="onPeriodInput"
          />
        </div>

        <!-- Cycle Name + Auto -->
        <div class="cycle-form-dialog__field" data-testid="cycles-page__form-name-field">
          <p class="cycle-form-dialog__label">Cycle Name*</p>
          <div
            class="cycle-form-dialog__name-wrap"
            :class="{ 'cycle-form-dialog__name-wrap--error': !!localErrors.description }"
            :data-testid="isAdd
              ? 'bookclosure-project-list__description-input'
              : 'bookclosure-project-list__edit-description-input'"
          >
            <input
              ref="cycleNameInput"
              v-model="form.description"
              type="text"
              class="cycle-form-dialog__name-input noTransform"
              data-testid="string-text-field"
              :disabled="loading"
              @input="onNameInput"
            />
            <button
              v-if="showAutoNameButton"
              type="button"
              class="cycle-form-dialog__auto-btn"
              :class="{ 'cycle-form-dialog__auto-btn--active': isNameAuto }"
              :disabled="loading || !form.from || !form.to"
              data-testid="cycles-page__form-auto-name-btn"
              @click.stop="applyAutoName"
            >
              Auto
            </button>
          </div>
          <div
            v-if="localErrors.description"
            class="v-messages error--text"
            role="alert"
          >
            {{ localErrors.description }}
          </div>
        </div>

        <!-- Type (add editable; edit locked) -->
        <div
          class="cycle-form-dialog__field"
          data-testid="cycles-page__form-type-field"
        >
          <div class="cycle-form-dialog__label-row">
            <p class="cycle-form-dialog__label mb-0">Type*</p>
            <v-tooltip
              v-if="isEditLocked"
              bottom
              content-class="tooltip-bottom"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  small
                  class="cycle-form-dialog__lock-icon"
                  data-testid="cycles-page__form-type-lock"
                >
                  mdi-lock-outline
                </v-icon>
              </template>
              <span>Field Is Locked</span>
            </v-tooltip>
          </div>
          <v-btn-toggle
            v-model="form.cycle_type"
            mandatory
            dense
            borderless
            class="cycle-form-dialog__type-toggle"
            :disabled="isEditLocked || loading"
            data-testid="cycles-page__form-type-toggle"
          >
            <v-btn
              v-for="opt in cycleTypeOptions"
              :key="opt.name"
              :value="opt.name"
              outlined
              class="cycle-form-dialog__type-btn text-none"
              :data-testid="`cycles-page__form-type--${opt.name}`"
            >
              {{ opt.displayText }}
            </v-btn>
          </v-btn-toggle>
          <input
            type="hidden"
            :value="form.cycle_type"
            data-testid="bookclosure-project-list__cycle-type-input"
          />
          <div
            v-if="localErrors.cycleType"
            class="v-messages error--text"
            role="alert"
          >
            {{ localErrors.cycleType }}
          </div>
        </div>

        <!-- Location -->
        <div
          v-if="showLocationField || isEditLocked"
          class="cycle-form-dialog__field"
          data-testid="cycles-page__form-location-field"
        >
          <p class="cycle-form-dialog__label">Location*</p>
          <div
            v-if="isEditLocked"
            class="cycle-form-dialog__locked-field"
            data-testid="bookclosure-project-list__location-locked"
          >
            <div class="cycle-form-dialog__locked-chips">
              <span
                v-for="label in lockedLocationLabels"
                :key="label"
                class="cycle-form-dialog__frequency-chip"
              >
                {{ label }}
              </span>
              <span
                v-if="!lockedLocationLabels.length"
                class="cycle-form-dialog__locked-value"
              >—</span>
            </div>
            <v-tooltip bottom content-class="tooltip-bottom">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  small
                  class="cycle-form-dialog__lock-icon"
                >
                  mdi-lock-outline
                </v-icon>
              </template>
              <span>Field Is Locked</span>
            </v-tooltip>
          </div>
          <ChipMultiSelect
            v-else
            v-model="form.location"
            :items="locationOptions"
            item-text="name"
            item-value="_id"
            :searchable="true"
            search-placeholder="Search"
            :error="localErrors.location"
            :disabled="loading"
            placeholder="Select Entity/Location"
            data-testid="bookclosure-project-list__location-dropdown"
            data-testid-prefix="cycles-page__form-location"
            @input="onLocationChange"
          />
        </div>

        <!-- Template (Populate Checklist) -->
        <div
          v-if="isAdd || isEditLocked"
          class="cycle-form-dialog__field"
          data-testid="cycles-page__form-template-field"
        >
          <p class="cycle-form-dialog__label">Template*</p>
          <div
            v-if="isEditLocked"
            class="cycle-form-dialog__locked-field"
            data-testid="bookclosure-project-list__template-locked"
          >
            <span class="cycle-form-dialog__locked-value">{{ lockedTemplateLabel }}</span>
            <v-tooltip bottom content-class="tooltip-bottom">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  small
                  class="cycle-form-dialog__lock-icon"
                >
                  mdi-lock-outline
                </v-icon>
              </template>
              <span>Field Is Locked</span>
            </v-tooltip>
          </div>
          <div
            v-else
            class="cycle-form-dialog__frequency-control"
            :class="{ 'cycle-form-dialog__frequency-control--error': !!localErrors.checklistfrom }"
          >
            <v-menu
              v-model="isTemplateMenuOpen"
              offset-y
              :close-on-content-click="false"
              :disabled="loading"
              content-class="cycle-form-dialog__frequency-menu-content"
              :nudge-width="0"
            >
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="cycle-form-dialog__frequency-trigger"
                  :class="{ 'cycle-form-dialog__frequency-trigger--open': isTemplateMenuOpen }"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="loading"
                  data-testid="bookclosure-project-list__populate-checklist-dropdown"
                >
                  <div class="cycle-form-dialog__frequency-chips-wrap">
                    <span
                      v-if="selectedTemplateLabel"
                      class="cycle-form-dialog__frequency-selected"
                    >
                      {{ selectedTemplateLabel }}
                    </span>
                    <span
                      v-else
                      class="cycle-form-dialog__frequency-placeholder"
                    >
                      Select template
                    </span>
                  </div>
                  <v-icon
                    size="16"
                    class="cycle-form-dialog__frequency-chevron"
                  >
                    mdi-chevron-down
                  </v-icon>
                </button>
              </template>

              <v-list
                class="cycle-form-dialog__frequency-menu pa-0 d-flex flex-column gap-1"
                data-testid="cycles-page__form-template-menu"
              >
                <v-list-item
                  v-for="item in populateChecklistItems"
                  :key="item._id"
                  class="cycle-form-dialog__frequency-menu-item cycle-form-dialog__frequency-menu-item--select"
                  :class="{
                    'cycle-form-dialog__frequency-menu-item--active':
                      String(form.checklistfrom) === String(item._id),
                  }"
                  :data-testid="`cycles-page__form-template-option-${item._id}`"
                  @click="selectTemplate(item._id)"
                >
                  <v-list-item-content class="py-0">
                    <v-list-item-title class="cycle-form-dialog__frequency-menu-label">
                      {{ item.name }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <div
            v-if="localErrors.checklistfrom"
            class="v-messages error--text mt-1"
            role="alert"
          >
            {{ localErrors.checklistfrom }}
          </div>
        </div>

        <div
          v-if="isAdd && form.checklistfrom === CHECKLIST_FROM.PREVIOUS_PROJECT"
          class="cycle-form-dialog__field"
          data-testid="cycles-page__form-previous-cycle-field"
        >
          <lb-dropdown
            v-model="form.previousCycleId"
            label="Select Project*"
            :items="previousCycles"
            itemtext="description"
            itemvalue="_id"
            :error="localErrors.project"
            :hidedetails="!localErrors.project"
            appendiconinner="mdi-chevron-down"
            :attach="false"
            data-testid="bookclosure-project-list__select-project-dropdown"
          />
        </div>

        <!-- Frequency = tag multi-select (API: checklistfromvalue) -->
        <div
          v-if="showFrequencyField"
          class="cycle-form-dialog__field"
          data-testid="bookclosure-project-list__tags-section"
        >
          <div class="cycle-form-dialog__label-row">
            <p class="cycle-form-dialog__label mb-0">Frequency*</p>
          </div>
          <div
            v-if="isEditLocked"
            class="cycle-form-dialog__locked-field"
            data-testid="cycles-page__form-frequency-locked"
          >
            <div class="cycle-form-dialog__locked-chips">
              <span
                v-for="tag in lockedFrequencyLabels"
                :key="tag"
                class="cycle-form-dialog__frequency-chip"
              >
                {{ tag }}
              </span>
              <span
                v-if="!lockedFrequencyLabels.length"
                class="cycle-form-dialog__locked-value"
              >—</span>
            </div>
            <v-tooltip bottom content-class="tooltip-bottom">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  small
                  class="cycle-form-dialog__lock-icon"
                >
                  mdi-lock-outline
                </v-icon>
              </template>
              <span>Field Is Locked</span>
            </v-tooltip>
          </div>
          <ChipMultiSelect
            v-else
            v-model="form.tags"
            :items="tagOptions"
            item-text="label"
            item-value="value"
            :searchable="true"
            search-placeholder="Search"
            :error="localErrors.tags"
            :disabled="loading"
            placeholder="Select frequency"
            data-testid="cycles-page__form-frequency-dropdown"
            data-testid-prefix="cycles-page__form-frequency"
            @input="onFrequencyChange"
          />
        </div>

        <!-- Email notifications -->
        <div
          class="cycle-form-dialog__field"
          data-testid="cycles-page__form-notification-field"
        >
          <v-checkbox
            v-model="form.notification"
            label="Enable Email Notifications"
            class="mt-0 pt-0"
            dense
            hide-details
            color="primary"
            :disabled="loading"
            data-testid="bookclosure-project-list__notification-checkbox"
          />
        </div>
      </div>
    </template>

    <template v-slot:actions>
      <v-spacer />
      <v-btn
        class="cycle-form-dialog__cancel-btn secondaryBtn"
        :disabled="loading"
        data-testid="cycles-page__form-cancel-btn"
        @click="onCancel"
      >
        Cancel
      </v-btn>
      <v-btn
        v-if="isAdd"
        class="ml-2 primaryBtn"
        :loading="loading"
        :disabled="loading || !canSubmit"
        data-testid="bookclosure-project-list__create-cycle-btn"
        @click="onSave"
      >
        Save
      </v-btn>
      <v-btn
        v-else
        color="primary"
        class="ml-2 text-none white--text"
        :loading="loading"
        :disabled="loading || !canSubmit"
        data-testid="bookclosure-project-list__update-cycle-btn"
        @click="onSave"
      >
        Save
      </v-btn>
    </template>
  </lb-drawer>
</template>

<script>
import './CycleFormDialog.scss';
import {
  CHECKLIST_FROM,
  CYCLE_TYPE_OPTIONS,
  POPULATE_CHECKLIST_ITEMS,
  TEMPLATE_OPTIONS,
} from '../../utils/cyclesConstants';
import {
  frequencyLabelsFromCycle,
  generateCycleName,
} from '../../utils/cyclesHelpers';
import { CYCLE_MESSAGES } from '../../../../utils/constants';
import { PeriodRangePicker, ChipMultiSelect } from '../../../../components';

function emptyForm() {
  return {
    _id: null,
    from: undefined,
    to: undefined,
    description: '',
    notification: false,
    checklistfrom: CHECKLIST_FROM.CHECKLIST_MASTER,
    cycle_type: 'FBC',
    location: [],
    tags: [],
    previousCycleId: null,
  };
}

export default {
  name: 'CycleFormDialog',

  components: {
    PeriodRangePicker,
    ChipMultiSelect,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'add',
      validator: (v) => ['add', 'edit'].includes(v),
    },
    cycle: {
      type: Object,
      default: null,
    },
    locations: {
      type: Array,
      default: () => [],
    },
    tags: {
      type: Array,
      default: () => [],
    },
    templates: {
      type: Array,
      default: () => POPULATE_CHECKLIST_ITEMS,
    },
    previousCycles: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      form: emptyForm(),
      localErrors: {},
      autoGeneratedName: '',
      autoNameEnabled: false,
      isHydrating: false,
      isTemplateMenuOpen: false,
      cycleTypeOptions: CYCLE_TYPE_OPTIONS,
      CHECKLIST_FROM,
    };
  },

  computed: {
    isAdd() {
      return this.mode === 'add';
    },

    isEditLocked() {
      return this.mode === 'edit';
    },

    dialogHeading() {
      return this.isAdd ? 'Add Cycle' : 'Edit Cycle';
    },

    populateChecklistItems() {
      if (this.templates && this.templates.length) {
        return this.templates.map((t) => ({
          _id: t._id || t.checklistfrom,
          name: t.name || t.label,
        }));
      }
      return POPULATE_CHECKLIST_ITEMS;
    },

    locationOptions() {
      return this.locations || [];
    },

    showLocationField() {
      return (
        this.form.checklistfrom === CHECKLIST_FROM.CHECKLIST_MASTER ||
        this.form.checklistfrom === CHECKLIST_FROM.BLANK
      );
    },

    showFrequencyField() {
      if (this.isEditLocked) {
        return this.form.checklistfrom === CHECKLIST_FROM.CHECKLIST_MASTER;
      }
      return this.isAdd && this.form.checklistfrom === CHECKLIST_FROM.CHECKLIST_MASTER;
    },

    tagOptions() {
      return (this.tags || []).map((tag) => {
        if (tag && typeof tag === 'object') {
          const value = tag.name || tag.label || tag._id;
          return { value, label: tag.label || tag.name || tag._id };
        }
        return { value: tag, label: tag };
      });
    },

    lockedLocationLabels() {
      const ids = Array.isArray(this.form.location) ? this.form.location : [];
      if (!ids.length) return [];
      return ids.map((id) => {
        const match = this.locationOptions.find((loc) => loc._id === id);
        return match?.name || id;
      }).filter(Boolean);
    },

    lockedTemplateLabel() {
      const match = TEMPLATE_OPTIONS.find(
        (opt) => opt._id === this.form.checklistfrom || opt.checklistfrom === this.form.checklistfrom,
      );
      if (match?.label) return match.label;
      const populate = this.populateChecklistItems.find(
        (item) => item._id === this.form.checklistfrom,
      );
      return populate?.name || '—';
    },

    selectedTemplateLabel() {
      const match = this.populateChecklistItems.find(
        (item) => String(item._id) === String(this.form.checklistfrom),
      );
      return match?.name || '';
    },

    lockedFrequencyLabels() {
      const selected = Array.isArray(this.form.tags) ? this.form.tags : [];
      if (!selected.length) return [];
      return selected.map((value) => {
        const match = this.tagOptions.find((tag) => tag.value === value);
        return match?.label || value;
      });
    },

    isNameAuto() {
      if (!this.autoNameEnabled || !this.autoGeneratedName) return false;
      return String(this.form.description || '') === String(this.autoGeneratedName);
    },

    showAutoNameButton() {
      const description = String(this.form.description || '').trim();
      if (!description) return true;
      return this.isNameAuto;
    },

    periodError() {
      return this.localErrors.from || this.localErrors.to || '';
    },

    canSubmit() {
      return !!(
        this.form.from &&
        this.form.to &&
        this.form.description &&
        String(this.form.description).trim() !== ''
      );
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.resetForm();
      }
    },

    errors: {
      deep: true,
      handler(val) {
        this.localErrors = { ...(val || {}) };
      },
    },

    'form.checklistfrom'(next) {
      if (this.localErrors.checklistfrom) {
        this.$delete(this.localErrors, 'checklistfrom');
      }
      if (this.localErrors.project) {
        this.$delete(this.localErrors, 'project');
      }
      if (this.localErrors.tags) {
        this.$delete(this.localErrors, 'tags');
      }
      if (this.localErrors.location) {
        this.$delete(this.localErrors, 'location');
      }
      // Frequency (tags) only applies to Tasklist Master
      if (next !== CHECKLIST_FROM.CHECKLIST_MASTER && !this.isHydrating) {
        this.form.tags = [];
      }
    },

    'form.description'(value) {
      if (this.isHydrating || !this.autoNameEnabled) return;
      if (String(value || '') !== String(this.autoGeneratedName || '')) {
        this.autoNameEnabled = false;
      }
    },
  },

  methods: {
    resetForm() {
      this.isHydrating = true;
      this.localErrors = { ...(this.errors || {}) };
      this.autoGeneratedName = '';
      this.autoNameEnabled = false;

      if (this.mode === 'edit' && this.cycle) {
        this.form = {
          ...emptyForm(),
          _id: this.cycle._id,
          from: this.cycle.from,
          to: this.cycle.to,
          description: this.cycle.description || '',
          notification: !!this.cycle.notification,
          cycle_type: this.cycle.cycle_type || 'FBC',
          checklistfrom: this.cycle.checklistfrom || CHECKLIST_FROM.CHECKLIST_MASTER,
          location: Array.isArray(this.cycle.location)
            ? [...this.cycle.location]
            : [],
          tags: frequencyLabelsFromCycle(this.cycle),
        };
        this.autoGeneratedName = generateCycleName(this.cycle.from);
      } else {
        this.form = emptyForm();
      }

      this.$nextTick(() => {
        this.isHydrating = false;
      });
    },

    onPeriodInput({ from, to }) {
      this.form.from = from;
      this.form.to = to;
      if (this.localErrors.from) this.$delete(this.localErrors, 'from');
      if (this.localErrors.to) this.$delete(this.localErrors, 'to');
      this.onPeriodChange();
    },

    onPeriodChange() {
      if (this.isHydrating) return;

      // A valid Period requires both `from` and `to`. Do not auto-generate the
      // Cycle Name on initial load, partial selections, or when the Period is
      // cleared. If a previously auto-generated name is still in the field,
      // reset it so a stale name is not shown for an invalid Period.
      if (!this.form.from || !this.form.to) {
        if (
          this.autoNameEnabled &&
          this.form.description === this.autoGeneratedName
        ) {
          this.form.description = '';
        }
        this.autoGeneratedName = '';
        this.autoNameEnabled = false;
        return;
      }

      const nextName = generateCycleName(this.form.from);
      if (this.autoNameEnabled) {
        this.form.description = nextName;
      } else if (
        !this.form.description ||
        this.form.description === this.autoGeneratedName
      ) {
        this.form.description = nextName;
        this.autoNameEnabled = true;
      }
      this.autoGeneratedName = nextName;
    },

    onNameInput() {
      if (!this.isHydrating && this.autoNameEnabled) {
        this.autoNameEnabled = false;
      }
      if (this.localErrors.description) {
        this.$delete(this.localErrors, 'description');
      }
    },

    isAutoFilledDescription() {
      return String(this.form.description || '') === String(this.autoGeneratedName || '');
    },

    onFrequencyChange() {
      if (this.localErrors.tags) {
        this.$delete(this.localErrors, 'tags');
      }
    },

    onLocationChange() {
      if (this.localErrors.location) {
        this.$delete(this.localErrors, 'location');
      }
    },

    selectTemplate(id) {
      this.form.checklistfrom = id;
      this.isTemplateMenuOpen = false;
      if (this.localErrors.checklistfrom) {
        this.$delete(this.localErrors, 'checklistfrom');
      }
    },

    applyAutoName() {
      if (!this.form.from || !this.form.to) return;

      const nextName = generateCycleName(this.form.from);
      if (this.autoNameEnabled) {
        this.autoNameEnabled = false;
        if (this.isAutoFilledDescription()) {
          this.form.description = '';
        }
        return;
      }

      this.form.description = nextName;
      this.autoGeneratedName = nextName;
      this.autoNameEnabled = true;
    },

    validate() {
      const errors = {};

      if (!this.form.from) {
        errors.from = CYCLE_MESSAGES.ERROR.PLEASE_SELECT_START_DATE;
      }
      if (!this.form.to) {
        errors.to = CYCLE_MESSAGES.ERROR.PLEASE_SELECT_END_DATE;
      }
      if (!this.form.description || String(this.form.description).trim() === '') {
        errors.description = CYCLE_MESSAGES.ERROR.PLEASE_ENTER_DESCRIPTION;
      }

      if (this.isAdd) {
        if (!this.form.checklistfrom) {
          errors.checklistfrom = CYCLE_MESSAGES.ERROR.SELECT_POPULATE_CHECKLIST;
        }

        if (
          this.form.checklistfrom === CHECKLIST_FROM.PREVIOUS_PROJECT &&
          (!this.form.previousCycleId || typeof this.form.previousCycleId !== 'string')
        ) {
          errors.project = CYCLE_MESSAGES.ERROR.NO_PROJECT_SELECTED;
        }

        if (
          this.form.checklistfrom === CHECKLIST_FROM.CHECKLIST_MASTER &&
          (!this.form.cycle_type || this.form.cycle_type === '')
        ) {
          errors.cycleType = CYCLE_MESSAGES.ERROR.PLEASE_SELECT_CYCLE_TYPE;
        }

        if (
          (this.form.checklistfrom === CHECKLIST_FROM.CHECKLIST_MASTER ||
            this.form.checklistfrom === CHECKLIST_FROM.BLANK) &&
          (!this.form.location || this.form.location.length === 0)
        ) {
          errors.location = CYCLE_MESSAGES.ERROR.PLEASE_SELECT_CYCLE_LOCATIONS;
        }

        if (
          this.form.checklistfrom === CHECKLIST_FROM.CHECKLIST_MASTER &&
          (!Array.isArray(this.form.tags) || this.form.tags.length === 0)
        ) {
          errors.tags = CYCLE_MESSAGES.ERROR.NO_TAGS_SELECTED;
        }
      }

      this.localErrors = errors;
      return Object.keys(errors).length === 0;
    },

    buildPayload() {
      const payload = {
        description: String(this.form.description || '').trim(),
        from: this.form.from,
        to: this.form.to,
      };

      if (!this.isAdd) {
        payload._id = this.form._id;
        payload.notification = !!this.form.notification;
        return payload;
      }

      payload.notification = !!this.form.notification;
      payload.checklistfrom = this.form.checklistfrom;

      if (this.form.checklistfrom === CHECKLIST_FROM.CHECKLIST_MASTER) {
        payload.cycle_type = this.form.cycle_type;
        payload.location = [...(this.form.location || [])];
        payload.checklistfromvalue = [...(this.form.tags || [])];
      } else if (this.form.checklistfrom === CHECKLIST_FROM.BLANK) {
        payload.location = [...(this.form.location || [])];
      } else if (this.form.checklistfrom === CHECKLIST_FROM.PREVIOUS_PROJECT) {
        payload.checklistfromvalue = this.form.previousCycleId;
      }

      return payload;
    },

    onSave() {
      if (!this.validate()) return;
      this.$emit('save', this.buildPayload());
    },

    onInput(val) {
      this.$emit('input', val);
      if (!val) this.$emit('cancel');
    },

    onCancel() {
      this.$emit('input', false);
      this.$emit('cancel');
    },
  },
};
</script>
